import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy, input, output, inject, OnChanges } from '@angular/core';

import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';

import {
  SupplierResponse,
  CreateSupplierRequest,
  UpdateSupplierRequest,
  CreateSupplierResponse,
} from '../../models/supplier.model';
import { SupplierService } from '../../services/supplier.service';
import { ToastMessageService } from '@fitness-force/api';

@Component({
  selector: 'lib-supplier-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    TranslateModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    TextareaModule
  ],
  template: `
    <p-dialog
      [visible]="visible()"
      [modal]="true"
      [closable]="true"
      [draggable]="false"
      [resizable]="false"
      [style]="{ width: '600px' }"
      (onHide)="onCancel.emit()">

      <ng-template pTemplate="header">
        <h3>{{ isEditMode ? ('RETAIL_BILLING.SUPPLIER.EDIT' | translate) : ('RETAIL_BILLING.SUPPLIER.CREATE' | translate) }}</h3>
      </ng-template>

      <div class="bg-primary-50 border-primary-100 border-1 border-round p-3 mb-4 flex align-items-center gap-2">
        <i class="pi pi-info-circle text-primary"></i>
        <span class="text-primary font-medium">
          {{ companyLevel() ? ('RETAIL_BILLING.PRODUCTS.COMPANY_LEVEL' | translate) : ('RETAIL_BILLING.PRODUCTS.CURRENT_TENANT' | translate) }}
        </span>
      </div>

      <form [formGroup]="supplierForm" class="grid">
        <div class="col-12 md:col-6">
          <label for="name_primary">{{ 'RETAIL_BILLING.SUPPLIER.NAME' | translate }}
            @if (isSecondaryLanguage()) {
              <span>({{ primaryLanguage() }})</span>
            }
            <sup class="text-red-500">*</sup>
          </label>
          <input
            pInputText
            id="name_primary"
            formControlName="name_primary_language"
            class="w-full mt-2"
            aria-label="supplier-name-primary-input" />
          @if (supplierForm.get('name_primary_language')?.invalid && supplierForm.get('name_primary_language')?.touched) {
            <small class="p-error">
              {{ 'RETAIL_BILLING.SUPPLIER.NAME' | translate }} is required
            </small>
          }
        </div>

        @if (isSecondaryLanguage()) {
          <div class="col-12 md:col-6">
            <label for="name_secondary">{{ 'RETAIL_BILLING.SUPPLIER.NAME' | translate }}
              <span>({{ secondaryLanguage() }})</span>
              <sup class="text-red-500">*</sup>
            </label>
            <input
              pInputText
              id="name_secondary"
              formControlName="name_secondary_language"
              class="w-full mt-2"
              dir="rtl"
              aria-label="supplier-name-secondary-input" />
            @if (supplierForm.get('name_secondary_language')?.invalid && supplierForm.get('name_secondary_language')?.touched) {
              <small class="p-error">
                {{ 'RETAIL_BILLING.SUPPLIER.NAME' | translate }} is required
              </small>
            }
          </div>
        }

        <div class="col-12 md:col-6">
          <label for="desc_primary">{{ 'RETAIL_BILLING.SUPPLIER.DESCRIPTION' | translate }}
            @if (isSecondaryLanguage()) {
              <span>({{ primaryLanguage() }})</span>
            }
          </label>
          <textarea
            pInputTextarea
            id="desc_primary"
            formControlName="description_primary_language"
            rows="3"
            class="w-full mt-2"
            aria-label="supplier-description-primary-input">
          </textarea>
        </div>

        @if (isSecondaryLanguage()) {
          <div class="col-12 md:col-6">
            <label for="desc_secondary">{{ 'RETAIL_BILLING.SUPPLIER.DESCRIPTION' | translate }}
              <span>({{ secondaryLanguage() }})</span>
            </label>
            <textarea
              pInputTextarea
              id="desc_secondary"
              formControlName="description_secondary_language"
              rows="3"
              class="w-full mt-2"
              dir="rtl"
              aria-label="supplier-description-secondary-input">
            </textarea>
          </div>
        }

        <div class="col-12 md:col-6">
          <label for="email">{{ 'RETAIL_BILLING.SUPPLIER.EMAIL' | translate }}</label>
          <input
            pInputText
            id="email"
            type="email"
            formControlName="email"
            class="w-full mt-2"
            aria-label="supplier-email-input" />
        </div>

        <div class="col-12 md:col-6">
          <label for="phone">{{ 'RETAIL_BILLING.SUPPLIER.PHONE' | translate }}</label>
          <input
            pInputText
            id="phone"
            formControlName="phone"
            class="w-full mt-2"
            aria-label="supplier-phone-input" />
        </div>

        <div class="col-12">
          <label for="address">{{ 'RETAIL_BILLING.SUPPLIER.ADDRESS' | translate }}</label>
          <textarea
            pInputTextarea
            id="address"
            formControlName="address"
            rows="2"
            class="w-full mt-2"
            aria-label="supplier-address-input">
          </textarea>
        </div>
      </form>

      <ng-template pTemplate="footer">
        <div class="flex justify-content-end gap-2">
          <p-button [label]="'RETAIL_BILLING.COMMON.CANCEL' | translate"
            icon="pi pi-times"
            [text]="true"
            (onClick)="onCancel.emit()"
            aria-label="cancel-button" />
          <p-button [label]="'RETAIL_BILLING.COMMON.SAVE' | translate"
            icon="pi pi-check"
            [loading]="saving"
            [disabled]="supplierForm.invalid"
            (onClick)="saveSupplier()"
            aria-label="save-supplier-button" />
        </div>
      </ng-template>
    </p-dialog>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SupplierFormComponent implements OnInit, OnDestroy, OnChanges {
  private readonly fb = inject(FormBuilder);
  private readonly supplierService = inject(SupplierService);
  private readonly toastService = inject(ToastMessageService);
  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  readonly visible = input(false);
  readonly supplier = input<SupplierResponse | null>(null);
  readonly tenantId = input<number>();
  readonly companyLevel = input<boolean>(true);
  readonly primaryLanguage = input.required<string>();
  readonly secondaryLanguage = input.required<string>();
  readonly isSecondaryLanguage = input(false);

  readonly visibleChange = output<boolean>();
  readonly onSave = output<SupplierResponse>();
  readonly onCancel = output<void>();

  supplierForm: FormGroup;
  saving = false;
  private destroy$ = new Subject<void>();

  get isEditMode(): boolean {
    return !!this.supplier();
  }

  constructor() {
    this.supplierForm = this.fb.group({
      name_primary_language: ['', Validators.required],
      name_secondary_language: [''],
      description_primary_language: [''],
      description_secondary_language: [''],
      email: [''],
      phone: [''],
      address: [''],
      is_active: [true]
    });
  }

  ngOnInit() {
    this.updateFormValidation();
  }

  ngOnChanges() {
    const supplier = this.supplier();
    const visible = this.visible();
    if (visible && supplier) {
      this.populateForm();
    } else if (visible && !supplier) {
      this.resetForm();
    }
  }

  updateFormValidation() {
    if (this.supplierForm) {
      const nameSecondaryControl = this.supplierForm.get('name_secondary_language');
      if (this.isSecondaryLanguage()) {
        nameSecondaryControl?.setValidators([Validators.required]);
      } else {
        nameSecondaryControl?.clearValidators();
      }
      nameSecondaryControl?.updateValueAndValidity();
    }
  }

  populateForm() {
    const supplier = this.supplier();
    if (supplier) {
      this.supplierForm.patchValue({
        name_primary_language: supplier.name_primary_language || '',
        name_secondary_language: supplier.name_secondary_language || '',
        description_primary_language: supplier.description_primary_language || '',
        description_secondary_language: supplier.description_secondary_language || '',
        email: supplier.email || '',
        phone: supplier.phone || '',
        address: supplier.address || '',
        is_active: supplier.is_active
      });
    }
  }

  resetForm() {
    this.supplierForm.reset({
      is_active: true
    });
  }

  saveSupplier() {
    if (this.supplierForm.valid) {
      this.saving = true;
      const formData = this.supplierForm.value;

      const request: CreateSupplierRequest | UpdateSupplierRequest = {
        name_primary_language: formData.name_primary_language || '',
        name_secondary_language: formData.name_secondary_language || '',
        description_primary_language: formData.description_primary_language || '',
        description_secondary_language: formData.description_secondary_language || '',
        email: formData.email || '',
        phone: formData.phone || '',
        address: formData.address || '',
        is_active: formData.is_active ?? true
      };

      const saveOperation = this.isEditMode
        ? this.supplierService.updateCompanySupplier(this.supplier()!.id, request as UpdateSupplierRequest)
        : this.supplierService.createCompanySupplier(request as CreateSupplierRequest);

      saveOperation
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: (response) => {
            this.saving = false;
            const newId = this.isEditMode
              ? this.supplier()?.id ?? 0
              : (response as CreateSupplierResponse).supplier_id ?? 0;
            const savedSupplier: SupplierResponse = {
              ...this.supplier(),
              id: newId,
              ...request,
              created_at: this.supplier()?.created_at || new Date().toISOString(),
              updated_at: new Date().toISOString(),
              created_by: this.supplier()?.created_by || 1,
              updated_by: 1,
              company_id: this.supplier()?.company_id || 1
            } as SupplierResponse;
            this.changeDetectorRef.detectChanges();
            this.onSave.emit(savedSupplier);
            this.visibleChange.emit(false);
          },
          error: (error) => {
            this.saving = false;
            this.toastService.showError(error);
            this.changeDetectorRef.detectChanges();
          }
        });
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.visibleChange.emit(false);
  }
}
